import * as React from 'react';
import { ChevronRight, Search, User, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  Announcement,
  AnnouncementTag,
  AnnouncementTitle,
} from "@/components/kibo-ui/announcement";
import {
  DashboardIcon,
  AssociadosIcon,
  EmpresasIcon,
  FinanceiroIcon,
  ColoniasIcon,
  ComunicacaoIcon,
  AdministracaoIcon,
  LogoutIcon,
} from '@/components/icons/CustomIcons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAutoUpdater } from '@/hooks/useAutoUpdater';
import { getAppVersion, isTauriRuntime } from '@/utils/runtime';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { GlobalSearch } from '@/components/ui/GlobalSearch';
import { Typewriter } from '@/components/ui/Typewriter';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarMenuBadge,
  SidebarMenu,
} from '@/components/ui/sidebar';

// Tipos para os itens de menu
type MenuSubItem = {
  title: string;
  href: string;
  description?: string;
  badge?: number;
  urgent?: boolean;
};

type MenuItemType = {
  title: string;
  href: string;
  icon: LucideIcon | React.ComponentType<{ className?: string; size?: number }>;
  description?: string;
  badge?: number;
  urgent?: boolean;
  subItems?: MenuSubItem[];
};

type SidebarMenuItem = MenuItemType;

const AppSidebar = () => {
  const [activeItem, setActiveItem] = React.useState<string>('/dashboard');
  const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = React.useState(false);
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { checkForUpdates, isChecking, updateAvailable, downloading: isUpdateDownloading } = useAutoUpdater();

  const isTauri = isTauriRuntime();

  React.useEffect(() => {
    if (isTauri) {
      checkForUpdates(false);
    }
  }, [isTauri, checkForUpdates]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Se no app nativo, o clique busca avaliações
    if (isTauri) {
      checkForUpdates(true);
      return;
    }

    setDownloading(true);
    try {
      const updaterEndpoint =
        import.meta.env.VITE_TAURI_UPDATER_ENDPOINT ||
        'https://xfglnsbhcyaoufwbwrco.supabase.co/storage/v1/object/public/apps/updater.json';

      const manifestResponse = await fetch(`${updaterEndpoint}?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!manifestResponse.ok) {
        throw new Error(`Falha ao carregar manifesto de update (${manifestResponse.status})`);
      }

      const manifest = await manifestResponse.json();
      const installerUrl =
        manifest?.installer?.url ||
        manifest?.installerUrl ||
        manifest?.installer_url ||
        null;

      if (installerUrl) {
        window.location.href = `${installerUrl}?t=${Date.now()}`;
        return;
      }

      const { data } = supabase.storage.from('apps').getPublicUrl('SINSECRJ_OS_latest.exe');
      if (data?.publicUrl) {
        window.location.href = `${data.publicUrl}?t=${Date.now()}`;
      }
    } catch (err) {
      console.error('Erro ao baixar, fallback local:', err);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  const menuItems: SidebarMenuItem[] = [
    {
      title: 'Dashboard',
      href: '/dashboard',
      icon: DashboardIcon,
      description: 'Visão geral do sistema',
      subItems: [],
    },
    {
      title: 'Associados',
      href: '/associados',
      icon: AssociadosIcon,
      description: 'Gestão de membros',
      subItems: [
        { title: 'Lista de Associados', href: '/associados' },
        { title: 'Relatórios', href: '/associados/relatorios' },
      ],
    },
    {
      title: 'Empresas',
      href: '/empresas',
      icon: EmpresasIcon,
      description: 'Empresas parceiras',
      subItems: [
        { title: 'Lista de Empresas', href: '/empresas' },
        { title: 'Relatórios', href: '/empresas/relatorios' },
      ],
    },
    {
      title: 'Financeiro',
      href: '/financeiro',
      icon: FinanceiroIcon,
      description: 'Controle financeiro',
      urgent: true,
      subItems: [
        { title: 'Visão Geral', href: '/financeiro' },
        { title: 'Contas a Receber', href: '/financeiro/contas-receber' },
        { title: 'Contas a Pagar', href: '/financeiro/contas-pagar' },
        { title: 'Fluxo de Caixa', href: '/financeiro/fluxo-caixa' },
        { title: 'Inadimplência', href: '/financeiro/inadimplencia' },
        { title: 'Gerar Cobranças Mensais', href: '/financeiro/gerar-cobrancas-mensais' },
        { title: 'Relatórios', href: '/financeiro/relatorios' },
      ],
    },
    {
      title: 'Colonias',
      href: '/colonias/dashboard',
      icon: ColoniasIcon,
      description: 'Gestão de colônias',
      subItems: [
        { title: 'Dashboard', href: '/colonias/dashboard' },
      ],
    },
    {
      title: 'Comunicação',
      href: '/comunicacao/Mensagens',
      icon: ComunicacaoIcon,
      description: 'Central de mensagens',
      subItems: [
        { title: 'Mensagens', href: '/comunicacao/Mensagens' },
      ],
    },
    {
      title: 'Administração',
      href: '/administracao/usuarios',
      icon: AdministracaoIcon,
      description: 'Configurações do sistema',
      subItems: [
        { title: 'Usuários', href: '/administracao/usuarios' },
        { title: 'Configurações', href: '/administracao/configuracoes' },
        { title: 'Controle', href: '/administracao/controle' },
        { title: 'Logs', href: '/administracao/logs' },
      ],
    },
  ];

  const toggleExpanded = (href: string) => {
    setExpandedItems((prev: Record<string, boolean>) => ({
      ...prev,
      [href]: !prev[href],
    }));
  };

  const handleItemClick = (item: SidebarMenuItem) => {
    if (item.subItems && item.subItems.length > 0) {
      toggleExpanded(item.href);
    } else {
      setActiveItem(item.href);
      navigate(item.href);
    }
  };

  const handleSubItemClick = (subItem: MenuSubItem) => {
    setActiveItem(subItem.href);
    navigate(subItem.href);
  };

  const getDownloadContent = () => {
    if (isTauri) {
      if (isUpdateDownloading) return 'Instalando Update...';
      if (updateAvailable) return 'Atualização Opcional (Clique)';
      if (isChecking) return 'Verificando versão...';
      return 'SINSECRJ OS Atualizado';
    }
    if (downloading) return 'Gerando Link...';
    return <Typewriter texts={['Baixe agora o App!', 'Novidades em breve.']} />;
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center px-2 py-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 p-0 overflow-hidden border-0 shadow-none mr-2"
              >
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name || 'Avatar'}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-4 w-4" />
                )}
                <span className="sr-only">Perfil do Usuário</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-4">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src={
                    user?.avatarUrl ||
                    `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email}`
                  }
                  alt={user?.name || 'Avatar'}
                  className="h-10 w-10 rounded-full"
                />
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-sm truncate">{user?.name || 'Usuário'}</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start mt-2"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('profile-modal-open'));
                }}
              >
                <User className="mr-2 h-4 w-4" />
                Perfil
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start mt-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                onClick={signOut}
              >
                <LogoutIcon className="size-4 mr-2" />
                Sair do sistema
              </Button>
            </PopoverContent>
          </Popover>
          <div className="grid flex-1 min-w-0 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{user?.name || 'Usuário'}</span>
            <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Search Section */}
        <SidebarGroup>
          <SidebarGroupContent>
            <div className="px-2 py-2">
              <Button
                variant="outline"
                className="relative h-8 w-full justify-start px-3 py-2 text-sm"
                onClick={() => {
                  const event = new CustomEvent('global-search-open');
                  window.dispatchEvent(event);
                }}
              >
                <Search className="h-4 w-4 mr-2" />
                <span className="sr-only">Buscar</span>
                <kbd className="pointer-events-none absolute right-1.5 top-1.5 h-5 select-none items-center gap-1 rounded border bg-muted px-1 font-mono text-[10px] font-medium opacity-100 flex">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Main Menu */}
        <SidebarGroup>
          <SidebarGroupLabel>Navegação</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    onClick={() => handleItemClick(item)}
                    isActive={activeItem === item.href}
                    tooltip={item.title}
                  >
                    <item.icon className="size-4" />
                    <span>{item.title}</span>
                    {item.badge && (
                      <SidebarMenuBadge className="ml-auto">{item.badge}</SidebarMenuBadge>
                    )}
                    {item.subItems && item.subItems.length > 0 && (
                      <ChevronRight
                        className={`ml-auto size-4 transition-transform ${expandedItems[item.href] ? 'rotate-90' : ''
                          }`}
                      />
                    )}
                  </SidebarMenuButton>

                  {item.subItems && item.subItems.length > 0 && expandedItems[item.href] && (
                    <SidebarMenuSub>
                      {item.subItems.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.href}>
                          <SidebarMenuSubButton
                            onClick={() => handleSubItemClick(subItem)}
                            isActive={activeItem === subItem.href}
                          >
                            <span>{subItem.title}</span>
                            {subItem.badge && (
                              <SidebarMenuBadge className="ml-auto">
                                {subItem.badge}
                              </SidebarMenuBadge>
                            )}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="px-2 py-1 space-y-2">
          {/* Botão de Download com Ajuste de Espaçamento Próximo */}
          <button
            onClick={handleDownload}
            disabled={(!isTauri && downloading) || (isTauri && (isChecking || isUpdateDownloading))}
            className="w-full text-left transition-all active:scale-[0.98] disabled:active:scale-100"
          >
            <Announcement className="w-full justify-start gap-4 border-sidebar-border bg-sidebar hover:bg-sidebar-accent group-data-[collapsible=icon]:hidden cursor-pointer shadow-none">
              <AnnouncementTag className="bg-blue-600 dark:bg-blue-700 text-white border-none shrink-0">
                v{getAppVersion()}
              </AnnouncementTag>
              <AnnouncementTitle className="flex items-center gap-2 overflow-hidden">
                <span className="truncate text-sidebar-foreground">
                  {getDownloadContent()}
                </span>
                {(!isTauri && downloading) || (isTauri && (isChecking || isUpdateDownloading)) ? (
                  <Loader2 className="shrink-0 size-3 animate-spin text-muted-foreground" />
                ) : null}
              </AnnouncementTitle>
            </Announcement>
          </button>
        </div>
      </SidebarFooter>

      {/* GlobalSearch Dialog - apenas o dialog invisível para funcionar */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
        <GlobalSearch />
      </div>
    </Sidebar>
  );
};

export default AppSidebar;
